// The little chip that follows a building on its tile, ONLY while a freshly dropped building awaits your decision:
// a ↻ Rotate button, then "Confirm placement", "Reposition" and "Cancel (refund…)". Once confirmed, the facing is final and
// clicking the tile does nothing special — the rotate icon is not offered again.
// All the rules (refund fee, free reposition, rotation steps) live in game/buildingManager.js.

import { Events, on } from './eventBus.js'
import { BuildingConfig } from '../config/balance.js'
import { getPending, confirmPending, cancelPending, repositionPending, rotateBuilding, cancelRefund } from '../game/buildingManager.js'
import { getBuilding, getBuildings } from '../game/buildingState.js'
import { getPlotType } from '../game/plotState.js'
import { getWealth } from '../game/seasonManager.js'
import { tileScreenPos } from '../scene/placement.js'
import { formatCoins } from './format.js'

const ROTATE_SVG = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.6-5.9"/><path d="M20 4v5h-5"/></svg>'

export function mountPlacementConfirmUI() {
  // DEV: read the APP's live state (a test that re-imports modules would get a second, separate copy).
  if (import.meta.env?.DEV) window.__place = { pending: getPending, building: getBuilding, buildings: getBuildings, plotType: getPlotType, wealth: getWealth }
  const el = document.createElement('div')
  el.className = 'place-pop'
  el.setAttribute('role', 'dialog')
  document.body.appendChild(el)

  let tile = null       // the building's tile key
  let raf = 0

  const place = () => {
    if (!tile) return
    const p = tileScreenPos(tile, 1.3)
    if (p) {
      const w = el.offsetWidth, h = el.offsetHeight
      const x = Math.min(Math.max(p.x - w / 2, 8), innerWidth - w - 8)
      const y = Math.min(Math.max(p.y - h - 14, 60), innerHeight - h - 8)
      el.style.transform = `translate(${x}px, ${y}px)`
    }
    raf = requestAnimationFrame(place)
  }

  const render = () => {
    const p = getPending()
    if (!p) return hide()
    const name = BuildingConfig[p.type]?.name ?? 'Building'
    el.className = 'place-pop on'
    el.innerHTML = `
      <div class="place-pop__name">${name}</div>
      <button class="place-pop__rot" data-rot type="button" aria-label="Rotate building" title="Rotate (R)">${ROTATE_SVG}</button>
      <div class="place-pop__hint">Rotate to choose its facing</div>
      <button class="place-pop__go" data-confirm type="button">Confirm placement</button>
      <button class="place-pop__alt" data-move type="button">Reposition</button>
      <button class="place-pop__cancel" data-cancel type="button">Cancel <i>(refund ${formatCoins(cancelRefund(p.price))})</i></button>`
  }

  const show = (t) => {
    tile = t
    render()
    cancelAnimationFrame(raf)
    place()
  }
  const hide = () => {
    tile = null
    cancelAnimationFrame(raf)
    el.className = 'place-pop'
    el.innerHTML = ''
  }

  el.addEventListener('click', (e) => {
    const b = e.target.closest('button')
    if (!b) return
    if (b.hasAttribute('data-rot')) { if (tile) rotateBuilding(tile, 1); return }
    if (b.hasAttribute('data-confirm')) confirmPending()
    else if (b.hasAttribute('data-move')) repositionPending()
    else if (b.hasAttribute('data-cancel')) cancelPending()
  })

  on(Events.PlacementPending, ({ tileIndex }) => show(tileIndex))
  on(Events.PlacementResolved, () => hide())
  on(Events.KingdomChange, hide)
  on(Events.BuildingRemoved, ({ tileIndex }) => { if (tile === tileIndex) hide() })
  document.addEventListener('keydown', (e) => {
    if (!tile || !getPending()) return
    if (e.key === 'r' || e.key === 'R') rotateBuilding(tile, 1)
    else if (e.key === 'Enter') confirmPending()
  })
}
